import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { news } from "../data/mock";

function formatDate(s) {
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return s; }
}

export default function BlogDetail() {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);
  if (!article) return <Navigate to="/news" replace />;
  const related = news.filter((n) => n.id !== article.id).slice(0, 3);

  return (
    <div className="relative">
      <Header />
      <PageHero
        eyebrow={article.category}
        title={article.title}
        subtitle={article.excerpt}
        image={article.cover}
        crumbs={[{ label: "News", to: "/news" }, { label: article.title }]}
      />

      {/* Meta */}
      <section className="py-8 bg-[#0a0a0c] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-6 text-sm text-white/60">
          <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-[#F5D000]" /> {formatDate(article.date)}</span>
          <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#F5D000]" /> {article.readTime} read</span>
          <span className="ml-auto px-3 py-1.5 rounded-full bg-[#F5D000]/10 border border-[#F5D000]/30 text-[#F5D000] text-xs font-semibold uppercase tracking-wider">
            {article.category}
          </span>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28 bg-[#08080a]">
        <div className="max-w-3xl mx-auto px-6 lg:px-0">
          <article className="prose-content space-y-6 text-white/80 text-lg leading-relaxed">
            {article.body.map((p, i) => (
              <p key={i} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>{p}</p>
            ))}
          </article>
          <div className="mt-14 pt-8 border-t border-white/8 flex items-center justify-between">
            <Link to="/news" className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white">
              <ArrowLeft className="w-4 h-4" /> All news
            </Link>
            <Link to="/#contact" className="btn-amber px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2">
              Get in touch <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 lg:py-28 bg-[#0a0a0c] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-10 reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              More <span className="text-amber-accent">stories</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="proj-card block rounded-xl overflow-hidden bg-[#101014] border border-white/5 group"
              >
                <div className="relative aspect-[16/10]">
                  <img src={n.cover} alt={n.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent" />
                </div>
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{n.category} · {formatDate(n.date)}</div>
                  <div className="font-display text-lg font-bold text-white group-hover:text-[#F5D000] transition-colors line-clamp-2">{n.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
}
